// packages/protocol/src/chat.ts
export const protocolChatConfig = {
  CHAT_TEXT_MAX_CODE_POINTS: 280 as const,
  CHAT_TEXT_MAX_BYTES: 1120 as const,
  CHAT_PING_WORLD_LIMIT: 256 * 32,
};

// packages/protocol/src/wire-schema.ts
export const protocolWireSchemaConfig = {
  WIRE_VERSION: 2 as const,
  RECEIPT_REASON_MAX: 128 as const,
  // Admission: simultaneous running matches limits
  V2_MAX_RUNNING_MATCHES_LIMIT: 256 as const,
  V2_MAX_RTT_MS: 60_000 as const,
  // HTTP room snapshots and WebSocket lobby updates share one bounded contract.
  START_COUNTDOWN_MS: 5000 as const,
  PUBLIC_ROOM_COUNTDOWN_MS: 30_000 as const,
  // Optional bounded detail: NO minLength (empty detail is legal), code-point

  // bound here; the native UTF-16 adapter policy comes later.
  ERROR_DETAIL_MAX: 512 as const,
};

// packages/protocol/src/wire.ts
export const protocolWireConfig = {
  // Wall-time view rate. An applied human intent adds one off-cadence view
  // (paced in the worker), so a click reaction never waits for the next slot.
  V2_PUBLISH_HZ: 30 as const,
  // Per-callback catch-up tick budget; simulation debt is retained up to
  // WORKER_MAX_DEBT_MS, ticks are never skipped (reuses the v1 starting-budget
  // idea).
  V2_TICK_CATCHUP: 8 as const,
  // Seat reservation after detected disconnection: exactly 90 seconds.
  V2_SEAT_GRACE_MS: 90_000 as const,
  // Admission: default and hard ceiling for simultaneously running matches.
  // V2_MAX_RUNNING_MATCHES_LIMIT mirrors apps/server/src/lobby.ts MAX_TOTAL_ROOMS (256);
  // src must not import server code, so keep the two in sync by hand.
  V2_MAX_RUNNING_MATCHES_DEFAULT: 1 as const,
  V2_MAX_INTENTS_PER_SEC_PER_OWNER: 30 as const,
  V2_MAX_QUEUED_INTENTS_PER_OWNER: 64 as const,
  V2_MAX_QUEUED_INTENT_BYTES_PER_OWNER: 262_144 as const,
  V2_MAX_INTENTS_PER_TICK_PER_OWNER: 8 as const,
  // Aggregate entities an admitted tick batch may touch across all owners.
  V2_MAX_AFFECTED_ENTITIES_PER_TICK: 1024 as const,
  // Per-owner final-receipt ring (duplicate-request dedupe + recovery window)
  // and the bounded per-message receipt query/recovery batch.
  V2_MAX_RECEIPTS_PER_OWNER: 128 as const,
  // Whole-message byte caps. Client messages are small; views carry a full
  // filtered world (bounded separately); welcome carries the full map layout.
  V2_MAX_MESSAGE_BYTES: 16_384 as const,
  V2_MAX_SERVER_MESSAGE_BYTES: 65_536 as const,
  // Measured on a max-size 256x256 map with 16,370 owned units: projectWorldView
  // serialized to 2,545,088 bytes. Keep 4 MiB for the full envelope and bounded
  // transient effects; no GPU rendering claim is made by this transport bound.
  V2_MAX_VIEW_MESSAGE_BYTES: 4_194_304 as const,
  // Baseline (fresh full view) request minimum spacing, enforced by the server
  // per owner; the parser only bounds the message itself.
  V2_BASELINE_MIN_INTERVAL_MS: 1_000 as const,
  // Finished replay retention in server memory (per match bytes, total count,
  // expiry). Provisional pending measurement; a restart may lose all of it.
  V2_REPLAY_RETAIN_MAX_BYTES: 8 * 1024 * 1024,
  V2_REPLAY_RETAIN_MAX_COUNT: 32 as const,
  V2_REPLAY_RETAIN_MS: 30 * 60 * 1000,
};

// packages/protocol/src/protocol.ts
export const protocolConfig = {
  PROTOCOL_VERSION: 1 as const,
  MAX_SLOTS: 8 as const,
  // Stamped into MatchConfig by v2 builders; kept for makeMatchConfig defaults
  // and apps/server/src/lobby.ts room construction.
  INPUT_DELAY_TURNS: 3 as const,
  MAX_LOOKAHEAD_EXTRA_TURNS: 5,
  HASH_EVERY_TURNS: 15 as const,
  // Welcome includes the full map and landscape: the 320x320 eight-player field
  // needs about 2 MB. Only the server's initial layout gets this 4 MiB allowance.
  MAX_WELCOME_BYTES: 4 * 1024 * 1024,
  // Match the snapshot tick bound; unlimited gameplay outlives replay storage.
  MAX_TURN: 1_000_000_000 as const,
  MAX_PAUSES_PER_SLOT: 3 as const,
  PAUSE_MAX_MS: 60000 as const,
  // Total pause time one slot may consume per match across all its pauses.
  // A pause ends itself when the single-pause cap or the slot's remaining
  // total runs out; the v2 worker enforces this (apps/server/src/worker/runtime.ts).
  PAUSE_TOTAL_MS: 120000 as const,
  BUILD_ID_MAX: 128 as const,
};

// Validation limits in packages/protocol/src/wire-schema.ts
export const protocolWireSchemaLimits = {
  ReceiptStatusV2Schema_seqs_maxItems: 32,
  RoomViewSchema_diagId_maxLength: 128,
  RoomViewSchema_code_maxLength: 32,
  RoomViewSchema_mapId_maxLength: 128,
  RoomViewSchema_owners_name_maxLength: 256,
  RoomViewSchema_owners_difficulty_maxLength: 64,
  RoomViewSchema_owners_emblem_maxLength: 128,
  RoomViewSchema_host_mapId_maxLength: 128,
  RoomViewSchema_cannotStartReason_maxLength: 128,
  RoomViewSchema_difficulties_maxLength: 64,
};

// Validation limits in packages/protocol/src/wire.ts
export const protocolWireLimits = {
  formationOrderCheck_maximum: 1_000_000,
};

export const wireValidationConfig = {
  worldMarginTiles: 4,
  fieldNameMax: 64,
};
